import type {ArticleCardLayoutsPayload, ItemStateValue, StarredResource, StateAware} from '../../types/types.ts';
import type {VideoPlayerOptions} from '../components/reel.ts';
import {ItemState} from '../models/item-state.ts';
import * as Layouts from '../services/layout.ts';
import * as User from '../services/user.ts';

let instance: Store;

export class Store {
	#itemStates: Map<string, ItemState>;
	#cardLayouts?: Record<string, ArticleCardLayoutsPayload>;
	#starredResources: StarredResource[] = [];

	constructor() {
		this.#itemStates = new Map();
	}

	async loadServerStatesForPrintEdition(editionId: string) {
		const statesResponse = await User.loadEditionStates(editionId);
		if (statesResponse.status === 200) {
			Object.entries(statesResponse.body).forEach(([articleId, stateObject]) => {
				const state = new ItemState(stateObject);
				try {
					saveToStorage(Store.getItemStateKey(articleId), state.toString());
				} catch (err) {
					console.log(err);
				}
			});
		}

		const cardLayoutsResponse = await Layouts.loadEditionCardLayouts(editionId);
		if (cardLayoutsResponse.status === 200) {
			this.#cardLayouts = cardLayoutsResponse.body;
		}
	}

	async loadServerStatesById(ids: string[]) {
		const stateResponse = await User.loadStatesById(ids);
		if (stateResponse.status !== 200) {
			return;
		}

		Object.entries(stateResponse.body).forEach(([articleId, stateObject]) => {
			const state = new ItemState(stateObject);
			try {
				saveToStorage(Store.getItemStateKey(articleId), state.toString());
			} catch (err) {
				console.log(err);
			}
		});
	}

	getItemState(id: string): ItemState {
		let state = this.#itemStates.get(id);

		if (!state) {
			const value = getFromStorage(Store.getItemStateKey(id));
			if (value) {
				state = ItemState.fromString(value);
			} else {
				state = new ItemState({id});
			}

			this.#itemStates.set(id, state);
		}

		return state;
	}

	resetItem(item: StateAware) {
		const itemState = item.getState();
		itemState.clearState();
		itemState.setStarred(false);
		itemState.setMobileProgress(0);
		itemState.setDesktopProgress(0);
		this.saveItemState(itemState);
	}

	clearItemState(item: StateAware) {
		const itemState = item.getState();
		itemState.clearState();
		this.saveItemState(itemState);
	}

	setItemState(item: StateAware, state: ItemStateValue) {
		const itemState = item.getState();
		itemState.setState(state);
		this.saveItemState(itemState);
	}

	setItemStarred(item: StateAware, starred: boolean) {
		const state = item.getState();
		state.setStarred(starred);
		this.saveItemState(state);
	}

	setItemProgressMobile(item: StateAware, lastPageSeen: number) {
		const state = item.getState();
		state.setMobileProgress(lastPageSeen);
		this.saveItemState(state);
	}

	setItemProgressDesktop(item: StateAware, lastPageSeen: number) {
		const state = item.getState();
		state.setDesktopProgress(lastPageSeen);
		this.saveItemState(state);
	}

	saveItemState(state: ItemState) {
		saveToStorage(Store.getItemStateKey(state.id), state.toString());
		User.saveItemState(state.toObject()).then((response) => {
			if (response.status !== 200) {
				console.log('Error saving item state to backend', response.body);
			}
		});
	}

	static getItemStateKey(id: string) {
		return `state:a:${id}`;
	}

	static async load() {
		if (!instance) {
			instance = new Store();
			await instance.loadStarredResourcesFromServer();
		}
		return instance;
	}

	getUserPreference(key: string) {
		return getFromStorage(`user-pref:${key}`);
	}

	setUserPreference(key: string, value: string) {
		saveToStorage(`user-pref:${key}`, value);
	}

	getVideoPlayerOptions() {
		return {
			muted: this.getUserPreference('video:muted') === 'muted'
		};
	}

	setVideoPlayerOptions(options: VideoPlayerOptions) {
		this.setUserPreference('video:muted', options.muted ? 'muted' : '');
	}

	setArticleCardLayouts(payload: ArticleCardLayoutsPayload) {
		Layouts.saveArticleCardLayouts(payload).then((response) => {
			if (response.status !== 200) {
				console.log('Error saving article card layouts to backend', response.body);
			}
		});
	}

	async loadStarredResourcesFromServer() {
		try {
			const response = await User.loadStarredResources();
			if (response.status === 200 && Array.isArray(response.body)) {
				this.#starredResources = response.body;
				saveToStorage('starred', JSON.stringify(this.#starredResources));
				return;
			}
		} catch (err) {
			console.error('Failed to load starred resources from server, falling back to local storage.', err);
		}
	}

	getStarredResources() {
		return this.#starredResources;
	}

	hasStarredResource(id: string) {
		return this.#starredResources.some((edition) => edition.id === id);
	}

	removeStar(id: string) {
		const index = this.#starredResources.findIndex((edition) => edition.id === id);
		if (index === -1) {
			return;
		}

		this.#starredResources.splice(index, 1);
		User.saveStarredResources(this.#starredResources).then((response) => {
			if (response.status !== 200) {
				console.log('Error saving starred resources to backend', response.body);
			}
		});
	}

	addStarredResource(data: StarredResource) {
		if (!this.hasStarredResource(data.id)) {
			this.#starredResources.push(data);
			User.saveStarredResources(this.#starredResources).then((response) => {
				if (response.status !== 200) {
					console.log('Error saving starred resources to backend', response.body);
				}
			});
		}
	}

	getArticleCardLayouts(id: string) {
		return this.#cardLayouts?.[id];
	}
}

function getFromStorage(key: string) {
	return localStorage.getItem(key);
}

function saveToStorage(key: string, value: string) {
	localStorage.setItem(key, value);
}
